<template>
  <div>
    <div
      v-if="isLoading"
      class="text-muted mb-3"
    >
      Loading budgets...
    </div>
    <div
      v-else-if="loadError"
      class="text-danger mb-3"
    >
      {{ loadError }}
    </div>

    <template v-else>
      <section
        v-for="group in groups"
        :key="group.kind"
      >
        <h6 class="sub-section-title">
          {{ getGroupTitle(group) }}
        </h6>

        <el-tabs
          v-if="showTabs(group)"
          v-model="activeTab[group.kind]"
          class="partner-budget-tabs"
        >
          <el-tab-pane
            v-for="balance in orderedBalances(group)"
            :key="balance.key"
            :label="tabLabel(group, balance)"
            :name="balance.key"
          >
            <PartnerMemberBudgetFields
              :group="group"
              :balance="balance"
              @update-balance="emit('update-balance', balance, $event)"
              @remove="emit('remove-balance', group, balance)"
            />
          </el-tab-pane>
        </el-tabs>

        <PartnerMemberBudgetFields
          v-else-if="group.balances[0]"
          :group="group"
          :balance="group.balances[0]"
          @update-balance="emit('update-balance', group.balances[0], $event)"
          @remove="emit('remove-balance', group, group.balances[0])"
        />
      </section>

      <p
        v-for="field in missingBudgetFields"
        :key="field"
        class="text-muted"
      >
        {{
          getPartnerFieldLabel({
            field,
            defaultValue: field === 'budgetRides' ? 'Rides budgets' : 'Credits budgets',
            required: false,
          })
        }}
        are not configured for this partner.
      </p>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, reactive, watch } from 'vue';
import moment from 'moment-timezone';

import { usePartner } from '@/use/partner';
import { getBalancePeriod, isRidesAllowanceKind } from '../Partners/Subsidy/budgetBalanceForm';
import PartnerMemberBudgetFields from './PartnerMemberBudgetFields.vue';
import {
  formatBudgetDate,
  toBalanceFormRow,
  type EditableBudgetGroup,
  type EditableBudgetRow,
  type PartnerBudgetBalancePatch,
} from './partnerMemberBudget';

const props = defineProps<{
  groups: EditableBudgetGroup[];
  isLoading: boolean;
  loadError: string;
}>();

const emit = defineEmits<{
  'update-balance': [balance: EditableBudgetRow, patch: PartnerBudgetBalancePatch];
  'remove-balance': [group: EditableBudgetGroup, balance: EditableBudgetRow];
}>();

const { partnerInfo, isPartnerFieldVisible, getPartnerFieldLabel } = usePartner();

const activeTab = reactive<Record<string, string>>({});

const currentDate = computed(() => moment.tz(partnerInfo.value?.timeZone || 'UTC').format('YYYY-MM-DD'));

const getBudgetFieldFromKind = (kind: string) => (isRidesAllowanceKind(kind) ? 'budgetRides' : 'budgetCredits');

const getGroupTitle = (group: EditableBudgetGroup) =>
  getPartnerFieldLabel({
    field: getBudgetFieldFromKind(group.kind),
    defaultValue: isRidesAllowanceKind(group.kind) ? 'Rides budgets' : 'Credits budgets',
    required: false,
  });

const missingBudgetFields = computed(() => {
  if (props.isLoading || props.loadError) {
    return [];
  }
  return (['budgetRides', 'budgetCredits'] as const).filter(
    (field) => isPartnerFieldVisible(field) && !props.groups.some((group) => getBudgetFieldFromKind(group.kind) === field),
  );
});

const orderedBalances = (group: EditableBudgetGroup) =>
  [...group.balances].sort(
    (left, right) => formatBudgetDate(left.beginningDate).localeCompare(formatBudgetDate(right.beginningDate)) || left.key.localeCompare(right.key),
  );

const showTabs = (group: EditableBudgetGroup) => group.balances.some((row) => row.id != null);

const tabLabel = (group: EditableBudgetGroup, balance: EditableBudgetRow) => {
  const period = getBalancePeriod(toBalanceFormRow(balance), currentDate.value);
  if (period === 'past') {
    return 'Past';
  }
  if (period === 'current') {
    return 'Current';
  }
  const futures = orderedBalances(group).filter((row) => getBalancePeriod(toBalanceFormRow(row), currentDate.value) === 'future');
  if (futures.length <= 1) {
    return 'Future';
  }
  const index = futures.findIndex((row) => row.key === balance.key);
  return index === 0 ? 'Next' : `Future ${index + 1}`;
};

const syncActiveTabs = () => {
  for (const group of props.groups) {
    const ordered = orderedBalances(group);
    if (activeTab[group.kind] && ordered.some((row) => row.key === activeTab[group.kind])) {
      continue;
    }
    const current = ordered.find((row) => row.id != null && getBalancePeriod(toBalanceFormRow(row), currentDate.value) === 'current');
    activeTab[group.kind] = (current ?? ordered[0])?.key ?? '';
  }
};

watch(
  () => props.groups.map((group) => `${group.kind}:${group.balances.map((row) => row.key).join(',')}`).join('|'),
  () => syncActiveTabs(),
  { immediate: true },
);
</script>

<style lang="scss" scoped>
.partner-budget-tabs {
  margin-bottom: 12px;
}
</style>
